import { AddressAutocomplete } from '@agentic-flow-studio/shared/components/job-plan/AddressAutocomplete';
import type { RouterOutputs } from '@agentic-flow-studio/shared/lib/trpc';
import type { Skill } from '@agentic-flow-studio/shared/types/job-plan';
import type { AddressCandidate, JobProposal } from '@agentic-flow-studio/shared/types/job-proposal';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { Checkbox } from '@agentic-flow-studio/shared/ui/checkbox';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agentic-flow-studio/shared/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@agentic-flow-studio/shared/ui/table';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  HelpCircle,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Trash2,
  User,
  UserCog,
} from 'lucide-react';

type OperatorProfile = RouterOutputs['jobPlan']['operators']['list'][number];

export interface EditedFieldsShape {
  title?: string;
  description?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  contactCompany?: string;
  externalRef?: string;
  serviceDate?: string;
  estimatedDurationMinutes?: number;
  operatorProfileId?: number | null;
}

interface IngestProposalsTableProps {
  proposals: JobProposal[];
  isLoading: boolean;
  skillMap: Map<number, Skill>;
  operators: OperatorProfile[];
  serviceDate: string;
  selectedIds: Set<number>;
  onToggleSelected: (id: number) => void;
  onToggleSelectAll: () => void;
  onConfirmSelected: () => void;
  onConfirmSingle: (id: number) => void;
  onReject: (id: number) => void;
  isConfirming: boolean;
  expandedId: number | null;
  onToggleExpanded: (id: number) => void;
  selectedCandidates: Record<number, AddressCandidate>;
  onSelectCandidate: (proposalId: number, candidate: AddressCandidate) => void;
  manualAddresses: Record<number, string>;
  onManualAddressChange: (proposalId: number, value: string) => void;
  editedFields: Record<number, EditedFieldsShape>;
  onEditField: (proposalId: number, patch: Partial<EditedFieldsShape>) => void;
}

const STATUS_META = {
  resolved: { icon: CheckCircle2, className: 'text-emerald-600', label: 'Resolved' },
  ambiguous: { icon: AlertTriangle, className: 'text-amber-600', label: 'Ambiguous address' },
  unresolved: { icon: HelpCircle, className: 'text-red-600', label: 'Needs address' },
} as const;

export function IngestProposalsTable({
  proposals,
  isLoading,
  skillMap,
  operators,
  serviceDate,
  selectedIds,
  onToggleSelected,
  onToggleSelectAll,
  onConfirmSelected,
  onConfirmSingle,
  onReject,
  isConfirming,
  expandedId,
  onToggleExpanded,
  selectedCandidates,
  onSelectCandidate,
  manualAddresses,
  onManualAddressChange,
  editedFields,
  onEditField,
}: IngestProposalsTableProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-foreground">
            Staged Job Proposals ({proposals.length})
          </span>
          {proposals.length > 0 && (
            <span className="text-[11px] text-muted-foreground">
              Review, uncheck anything you don't want, then confirm
            </span>
          )}
        </div>

        {proposals.length > 0 && (
          <Button
            size="sm"
            onClick={onConfirmSelected}
            disabled={isConfirming || selectedIds.size === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 gap-1.5 shadow-xs"
          >
            {isConfirming ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5" />
            )}
            <span>Confirm Selected ({selectedIds.size})</span>
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8 text-muted-foreground gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-xs">Loading staged proposals...</span>
        </div>
      ) : proposals.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-8 text-center border border-dashed border-border/60 rounded-xl bg-card">
          <FileText className="h-8 w-8 text-muted-foreground/40 mb-2" />
          <p className="text-xs font-medium text-foreground">No staged proposals pending</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Paste a screenshot from your legacy software or drop a spreadsheet above to extract
            jobs.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-border/70 bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      selectedIds.size === 0
                        ? false
                        : selectedIds.size === proposals.length
                          ? true
                          : 'indeterminate'
                    }
                    onCheckedChange={onToggleSelectAll}
                    aria-label="Select all proposals"
                  />
                </TableHead>
                <TableHead className="w-10 text-[11px]">#</TableHead>
                <TableHead className="w-8" />
                <TableHead className="text-[11px]">Job</TableHead>
                <TableHead className="text-[11px]">Operator</TableHead>
                <TableHead className="text-[11px]">Skills</TableHead>
                <TableHead className="text-[11px] w-20">Duration</TableHead>
                <TableHead className="w-16 text-right text-[11px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {proposals.map((p, idx) => (
                <IngestProposalRow
                  key={p.id}
                  index={idx + 1}
                  proposal={p}
                  skillMap={skillMap}
                  operators={operators}
                  serviceDate={serviceDate}
                  isSelected={selectedIds.has(p.id)}
                  onToggleSelected={() => onToggleSelected(p.id)}
                  onConfirmSingle={() => onConfirmSingle(p.id)}
                  onReject={() => onReject(p.id)}
                  isConfirming={isConfirming}
                  isExpanded={expandedId === p.id}
                  onToggleExpanded={() => onToggleExpanded(p.id)}
                  chosenCandidate={selectedCandidates[p.id]}
                  onSelectCandidate={(cand) => onSelectCandidate(p.id, cand)}
                  manualAddress={manualAddresses[p.id]}
                  onManualAddressChange={(val) => onManualAddressChange(p.id, val)}
                  overrides={editedFields[p.id] || {}}
                  onEditField={(patch) => onEditField(p.id, patch)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

interface IngestProposalRowProps {
  index: number;
  proposal: JobProposal;
  skillMap: Map<number, Skill>;
  operators: OperatorProfile[];
  serviceDate: string;
  isSelected: boolean;
  onToggleSelected: () => void;
  onConfirmSingle: () => void;
  onReject: () => void;
  isConfirming: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  chosenCandidate?: AddressCandidate;
  onSelectCandidate: (candidate: AddressCandidate) => void;
  manualAddress?: string;
  onManualAddressChange: (value: string) => void;
  overrides: EditedFieldsShape;
  onEditField: (patch: Partial<EditedFieldsShape>) => void;
}

function IngestProposalRow({
  index,
  proposal: p,
  skillMap,
  operators,
  serviceDate,
  isSelected,
  onToggleSelected,
  onConfirmSingle,
  onReject,
  isConfirming,
  isExpanded,
  onToggleExpanded,
  chosenCandidate,
  onSelectCandidate,
  manualAddress,
  onManualAddressChange,
  overrides,
  onEditField,
}: IngestProposalRowProps) {
  const grade = p.confidenceGrade;
  const status = STATUS_META[grade];
  const StatusIcon = status.icon;
  const operatorProfileId =
    overrides.operatorProfileId !== undefined ? overrides.operatorProfileId : p.suggestedOperatorId;
  const operatorLabel = operatorProfileId
    ? operators.find((op) => op.id === operatorProfileId)?.user?.fullName ||
      operators.find((op) => op.id === operatorProfileId)?.user?.email ||
      `Operator #${operatorProfileId}`
    : 'Unassigned';

  return (
    <>
      <TableRow
        data-state={isSelected ? 'selected' : undefined}
        className="cursor-pointer"
        onClick={onToggleExpanded}
      >
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelected}
            aria-label={`Select ${p.title}`}
          />
        </TableCell>
        <TableCell className="text-xs text-muted-foreground font-mono">{index}</TableCell>
        <TableCell>
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <span title={status.label}>
              <StatusIcon className={`h-3.5 w-3.5 shrink-0 ${status.className}`} />
            </span>
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-semibold text-foreground truncate max-w-xs">
                {overrides.title ?? p.title}
              </span>
              <span className="text-[11px] text-muted-foreground truncate max-w-xs">
                {p.formattedAddress || 'No address extracted'}
              </span>
            </div>
          </div>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">{operatorLabel}</TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {p.suggestedSkillIds.length + p.unmatchedSkills.length || '—'}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {Math.round(p.estimatedDurationSeconds / 60)}m
        </TableCell>
        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              onClick={onConfirmSingle}
              disabled={isConfirming}
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white gap-1 px-2"
            >
              <CheckCircle2 className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:bg-destructive/10"
              onClick={onReject}
              title="Discard Proposal"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </TableCell>
      </TableRow>

      {isExpanded && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={8} className="bg-muted/20 p-4">
            <div className="flex flex-col gap-3">
              {/* Title & description */}
              <div className="flex flex-col gap-1.5">
                <Input
                  value={overrides.title ?? p.title}
                  onChange={(e) => onEditField({ title: e.target.value })}
                  className="h-8 text-sm font-semibold max-w-md"
                />
                <textarea
                  value={overrides.description ?? p.description ?? ''}
                  onChange={(e) => onEditField({ description: e.target.value })}
                  placeholder="Job scope / description / access notes..."
                  rows={2}
                  className="w-full max-w-2xl p-2 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
                {p.externalRef && (
                  <Badge variant="outline" className="text-[10px] font-mono w-fit">
                    #{p.externalRef}
                  </Badge>
                )}
              </div>

              {/* Customer contact */}
              <div className="p-2.5 rounded-lg border border-border/60 bg-background grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <span className="col-span-2 sm:col-span-4 text-[11px] font-semibold text-foreground/80 flex items-center gap-1">
                  <User className="h-3 w-3 text-primary" />
                  <span>Customer</span>
                </span>
                <Input
                  placeholder="Contact Name"
                  value={overrides.contactName ?? p.contactName ?? ''}
                  onChange={(e) => onEditField({ contactName: e.target.value })}
                  className="h-7 text-[11px]"
                />
                <Input
                  placeholder="Phone"
                  value={overrides.contactPhone ?? p.contactPhone ?? ''}
                  onChange={(e) => onEditField({ contactPhone: e.target.value })}
                  className="h-7 text-[11px]"
                />
                <Input
                  placeholder="Email"
                  value={overrides.contactEmail ?? p.contactEmail ?? ''}
                  onChange={(e) => onEditField({ contactEmail: e.target.value })}
                  className="h-7 text-[11px]"
                />
                <Input
                  placeholder="Company"
                  value={overrides.contactCompany ?? p.contactCompany ?? ''}
                  onChange={(e) => onEditField({ contactCompany: e.target.value })}
                  className="h-7 text-[11px]"
                />
              </div>

              {/* Operator assignment */}
              <div className="p-2.5 rounded-lg border border-border/60 bg-background flex flex-wrap items-center gap-2 text-xs">
                <span className="text-[11px] font-semibold text-foreground/80 flex items-center gap-1">
                  <UserCog className="h-3 w-3 text-primary" />
                  <span>Operator:</span>
                </span>
                <Select
                  value={operatorProfileId?.toString() ?? 'unassigned'}
                  onValueChange={(val) =>
                    onEditField({ operatorProfileId: val === 'unassigned' ? null : Number(val) })
                  }
                >
                  <SelectTrigger className="h-7 text-xs w-auto min-w-[180px]">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {operators.map((op) => (
                      <SelectItem key={op.id} value={op.id.toString()}>
                        {op.user?.fullName || op.user?.email || `Operator #${op.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!p.suggestedOperatorId && p.operatorDisplayName && (
                  <Badge
                    variant="outline"
                    className="text-[10px] text-muted-foreground border-dashed"
                  >
                    Source said: {p.operatorDisplayName}
                  </Badge>
                )}
              </div>

              {/* Address resolution */}
              <div className="flex flex-col gap-1.5 text-xs">
                {grade === 'resolved' && p.formattedAddress && (
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span className="font-medium text-foreground">{p.formattedAddress}</span>
                  </div>
                )}

                {grade === 'ambiguous' && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[11px] font-medium text-muted-foreground">
                      Multiple matches found (ordered by distance from depot):
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {p.candidates.map((cand, cidx) => {
                        const isSelected =
                          chosenCandidate?.formattedAddress === cand.formattedAddress ||
                          (!chosenCandidate && cidx === 0);
                        return (
                          <Button
                            key={cidx}
                            type="button"
                            variant={isSelected ? 'default' : 'outline'}
                            size="sm"
                            className="h-7 text-xs gap-1.5 text-left"
                            onClick={() => onSelectCandidate(cand)}
                          >
                            <MapPin className="h-3 w-3" />
                            <span>{cand.formattedAddress}</span>
                            {cand.distanceMetersFromDepot != null && (
                              <span className="opacity-75 font-mono text-[10px]">
                                ({Math.round(cand.distanceMetersFromDepot / 1000)}km)
                              </span>
                            )}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {grade === 'unresolved' && (
                  <div className="w-full max-w-xl">
                    <AddressAutocomplete
                      value={manualAddress ?? p.formattedAddress ?? ''}
                      onChange={onManualAddressChange}
                      onSelectAddress={(res) => {
                        onManualAddressChange(res.formattedAddress);
                        onSelectCandidate({
                          formattedAddress: res.formattedAddress,
                          latitude: res.latitude ?? 0,
                          longitude: res.longitude ?? 0,
                          googlePlaceId: res.googlePlaceId ?? '',
                          rankingBasis: 'provider_relevance',
                        });
                      }}
                      placeholder="Search or enter address manually..."
                      className="h-8 text-xs"
                    />
                  </div>
                )}
              </div>

              {/* Timing, priority, and skills */}
              <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border/40 text-[11px] text-muted-foreground">
                <div className="flex items-center gap-3">
                  <span>{p.serviceDate || serviceDate}</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{Math.round(p.estimatedDurationSeconds / 60)} min</span>
                  </span>
                  {p.priority > 0 && (
                    <Badge
                      variant="outline"
                      className={
                        p.priority === 2
                          ? 'text-red-600 border-red-500/30 bg-red-500/10'
                          : 'text-amber-600 border-amber-500/30 bg-amber-500/10'
                      }
                    >
                      {p.priority === 2 ? 'Urgent / Emergency' : 'High Priority'}
                    </Badge>
                  )}
                  {p.contactCompany && (
                    <span className="inline-flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      <span>{p.contactCompany}</span>
                    </span>
                  )}
                  {p.contactPhone && (
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Phone className="h-3 w-3 text-emerald-600" />
                      <span>{p.contactPhone}</span>
                    </span>
                  )}
                  {p.contactEmail && (
                    <span className="inline-flex items-center gap-1">
                      <Mail className="h-3 w-3 text-blue-600" />
                      <span>{p.contactEmail}</span>
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-1">
                  {p.suggestedSkillIds.map((sId) => {
                    const skill = skillMap.get(sId);
                    return (
                      <Badge
                        key={sId}
                        variant="outline"
                        style={{
                          backgroundColor: skill?.color ? `${skill.color}15` : undefined,
                          color: skill?.color || undefined,
                          borderColor: skill?.color ? `${skill.color}40` : undefined,
                        }}
                        className="text-[10px]"
                      >
                        {skill?.name || `Skill #${sId}`}
                      </Badge>
                    );
                  })}
                  {p.unmatchedSkills.map((um, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="text-[10px] text-muted-foreground border-dashed"
                    >
                      Unmatched: {um}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
